// 2026-06-25: Phase 9 — Non-Conformance log page
// List all NCs + create + dispose (rework spawns rework order)
import { cleanCustomerId } from '../../Services/app/operatorservice';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  listNcs, createNc, disposeNc, listWorkOrders, listWorkCenters, getWorkOrderDetail,
} from '../../Services/app/workorderservice';
import Swal from 'sweetalert2';
import './NcLog.css';

const NC_TYPES = ['DIMENSIONAL', 'VISUAL', 'FUNCTIONAL', 'MATERIAL', 'OTHER'];
const SEVERITY = ['MINOR', 'MAJOR', 'CRITICAL'];
const DISPOSITIONS = ['REWORK', 'SCRAP', 'USE_AS_IS', 'RTV'];

const SEVERITY_COLOR = { MINOR: '#9ca3af', MAJOR: '#f59e0b', CRITICAL: '#dc2626' };
const DISPO_COLOR = {
  PENDING: '#f59e0b', REWORK: '#2563eb', SCRAP: '#dc2626',
  USE_AS_IS: '#16a34a', RTV: '#9333ea',
};

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const ist = new Date(d.getTime() + 5.5 * 3600_000);
  return ist.toISOString().slice(0, 16).replace('T', ' ');
}

export default function NcLog() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [ncs, setNcs] = useState([]);
  const [wos, setWos] = useState([]);
  const [wcs, setWcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [showCreate, setShowCreate] = useState(false);
  const [disposingNc, setDisposingNc] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        listNcs(customerId),
        listWorkOrders(customerId).catch(() => ({ rows: [] })),
        listWorkCenters(customerId).catch(() => []),
      ]);
      setNcs(a || []);
      setWos(Array.isArray(b) ? b : (b?.rows || b?.data || []));
      setWcs(c || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [customerId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return ncs;
    return ncs.filter((n) => n.disposition === filter);
  }, [ncs, filter]);

  const counts = useMemo(() => {
    const c = { ALL: ncs.length, PENDING: 0, REWORK: 0, SCRAP: 0, USE_AS_IS: 0, RTV: 0 };
    ncs.forEach((n) => { if (c[n.disposition] !== undefined) c[n.disposition]++; });
    return c;
  }, [ncs]);

  return (
    <div className="nc-page pages" style={{ paddingTop: 32 }}>
      <div className="nc-header">
        <div>
          <h5 style={{ margin: 0 }}>Non-Conformance Log</h5>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Per-operation NC capture · disposition drives rework / scrap / use-as-is
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="nc-btn-secondary" onClick={fetchAll} disabled={loading}>Refresh</button>
          <button className="nc-btn-primary" onClick={() => setShowCreate(true)}>+ New NC</button>
        </div>
      </div>

      <div className="nc-tabs">
        {['ALL', 'PENDING', 'REWORK', 'SCRAP', 'USE_AS_IS', 'RTV'].map((t) => (
          <button
            key={t}
            className={`nc-tab ${filter === t ? 'nc-tab-active' : ''}`}
            onClick={() => setFilter(t)}
            style={filter === t && t !== 'ALL' ? { borderBottomColor: DISPO_COLOR[t] } : {}}
          >
            {t} <span className="nc-tab-count">{counts[t] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? <div style={{ padding: 30, color: '#9ca3af' }}>Loading…</div> :
        filtered.length === 0 ? <div style={{ padding: 30, color: '#9ca3af' }}>No NCs in this view.</div> : (
        <div className="nc-grid">
          {filtered.map((n) => (
            <div key={n.id} className="nc-card" style={{ borderLeftColor: SEVERITY_COLOR[n.severity] }}>
              <div className="nc-card-head">
                <div>
                  <div className="nc-num">{n.nc_number}</div>
                  <div className="nc-meta">{fmtTime(n.created_ts)}</div>
                </div>
                <span className="nc-pill" style={{ background: DISPO_COLOR[n.disposition] }}>{n.disposition}</span>
              </div>
              <div className="nc-row"><span>WO</span><strong>{n.wo_number} {n.op_seq}</strong></div>
              <div className="nc-row"><span>Operation</span><strong>{n.op_name}</strong></div>
              <div className="nc-row"><span>Work Center</span><strong>{n.work_center_code || '—'}</strong></div>
              <div className="nc-row"><span>Type / Severity</span>
                <strong>
                  {n.nc_type} ·
                  <span style={{ color: SEVERITY_COLOR[n.severity], marginLeft: 4 }}>{n.severity}</span>
                </strong>
              </div>
              <div className="nc-row"><span>Qty affected</span><strong>{n.qty_affected}</strong></div>
              {n.unit_cost > 0 && (
                <div className="nc-row"><span>Est. cost</span><strong>₹{(n.qty_affected * n.unit_cost).toLocaleString('en-IN')}</strong></div>
              )}
              {n.description && <div className="nc-desc">{n.description}</div>}
              {n.rework_order_id && (
                <div className="nc-rw-link">→ Rework spawned</div>
              )}
              {n.disposition === 'PENDING' && (
                <div className="nc-actions">
                  <button className="nc-btn-primary" onClick={() => setDisposingNc(n)}>Dispose</button>
                </div>
              )}
              {n.disposition !== 'PENDING' && n.disposition_notes && (
                <div className="nc-dispo-notes">
                  <em>By:</em> {n.dispositioned_by || '—'} · {n.disposition_notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <NcCreateDialog
          wos={wos}
          customerId={customerId}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchAll(); }}
        />
      )}

      {disposingNc && (
        <NcDisposeDialog
          nc={disposingNc}
          wcs={wcs}
          customerId={customerId}
          onClose={() => setDisposingNc(null)}
          onDisposed={() => { setDisposingNc(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

// ─── Create NC dialog ────────────────────────────────────────────────────────
function NcCreateDialog({ wos, customerId, onClose, onCreated }) {
  const [woId, setWoId] = useState('');
  const [opId, setOpId] = useState('');
  const [ops, setOps] = useState([]);
  const [form, setForm] = useState({
    nc_type: 'DIMENSIONAL',
    severity: 'MINOR',
    qty_affected: 1,
    description: '',
    unit_cost: 0,
    reported_by_operator_name: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!woId) { setOps([]); return; }
    (async () => {
      try {
        const detail = await getWorkOrderDetail(customerId, woId);
        setOps(Array.isArray(detail?.operations) ? detail.operations : []);
      } catch (e) { console.error(e); setOps([]); }
    })();
  }, [woId, customerId]);

  const submit = async () => {
    if (!woId || !opId) { Swal.fire('Missing', 'Select WO + operation', 'warning'); return; }
    setSaving(true);
    try {
      await createNc({ ...form, customer_id: customerId, wo_id: woId, op_id: opId });
      Swal.fire({ icon: 'success', title: 'NC logged', timer: 1200, showConfirmButton: false });
      onCreated();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nc-modal-head">
          <h6>New Non-Conformance</h6>
          <button onClick={onClose}>×</button>
        </div>
        <div className="nc-modal-body">
          <label>Work Order</label>
          <select value={woId} onChange={(e) => { setWoId(e.target.value); setOpId(''); }}>
            <option value="">— Select WO —</option>
            {wos.map((w) => <option key={w.id} value={w.id}>{w.wo_number} — {w.part_number}</option>)}
          </select>
          <label>Operation</label>
          <select value={opId} onChange={(e) => setOpId(e.target.value)} disabled={!woId}>
            <option value="">— Select Op —</option>
            {ops.map((o) => <option key={o.id} value={o.id}>{o.op_seq} — {o.op_name}</option>)}
          </select>
          <div className="nc-grid-2">
            <div>
              <label>Type</label>
              <select value={form.nc_type} onChange={(e) => setForm({ ...form, nc_type: e.target.value })}>
                {NC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label>Severity</label>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                {SEVERITY.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label>Qty Affected</label>
              <input type="number" min="1" value={form.qty_affected}
                     onChange={(e) => setForm({ ...form, qty_affected: Number(e.target.value) })} />
            </div>
            <div>
              <label>Unit Cost (₹)</label>
              <input type="number" min="0" value={form.unit_cost}
                     onChange={(e) => setForm({ ...form, unit_cost: Number(e.target.value) })} />
            </div>
          </div>
          <label>Reported by</label>
          <input type="text" value={form.reported_by_operator_name}
                 onChange={(e) => setForm({ ...form, reported_by_operator_name: e.target.value })}
                 placeholder="Operator name" />
          <label>Description</label>
          <textarea rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="What went wrong? e.g. 'Bore diameter oversize by 0.05mm — both sides'" />
        </div>
        <div className="nc-modal-foot">
          <button className="nc-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="nc-btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Log NC'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Dispose NC dialog ───────────────────────────────────────────────────────
function NcDisposeDialog({ nc, wcs, customerId, onClose, onDisposed }) {
  const [disposition, setDisposition] = useState('REWORK');
  const [reworkOpName, setReworkOpName] = useState(`Rework: ${nc.op_name}`);
  const [targetWcId, setTargetWcId] = useState(nc.work_center_id || '');
  const [estCost, setEstCost] = useState(nc.qty_affected * Number(nc.unit_cost || 0));
  const [dispBy, setDispBy] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!disposition || !dispBy) { Swal.fire('Missing', 'Disposition + your name required', 'warning'); return; }
    setSaving(true);
    try {
      const body = {
        customer_id: customerId,
        disposition,
        dispositioned_by: dispBy,
        disposition_notes: notes,
        ...(disposition === 'REWORK' && {
          rework_op_name: reworkOpName,
          target_work_center_id: targetWcId,
          estimated_cost: Number(estCost) || 0,
        }),
      };
      const r = await disposeNc(nc.id, body);
      Swal.fire({
        icon: 'success',
        title: disposition === 'REWORK' ? 'Rework Order spawned' : 'NC dispositioned',
        text: r.rework_order ? `Rework Order: ${r.rework_order.rework_wo_number}` : '',
        timer: 2000,
        showConfirmButton: false,
      });
      onDisposed();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nc-modal-head">
          <h6>Dispose {nc.nc_number}</h6>
          <button onClick={onClose}>×</button>
        </div>
        <div className="nc-modal-body">
          <div className="nc-dispose-summary">
            <div><strong>{nc.wo_number}</strong> · {nc.op_seq} {nc.op_name}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {nc.nc_type} · {nc.severity} · {nc.qty_affected} pcs · {nc.description}
            </div>
          </div>
          <label>Disposition</label>
          <div className="nc-dispo-options">
            {DISPOSITIONS.map((d) => (
              <button
                key={d}
                type="button"
                className={`nc-dispo-btn ${disposition === d ? 'nc-dispo-btn-active' : ''}`}
                onClick={() => setDisposition(d)}
                style={disposition === d ? { background: DISPO_COLOR[d], color: '#fff', borderColor: DISPO_COLOR[d] } : {}}
              >{d}</button>
            ))}
          </div>
          {disposition === 'REWORK' && (
            <>
              <label>Rework operation name</label>
              <input type="text" value={reworkOpName} onChange={(e) => setReworkOpName(e.target.value)} />
              <label>Target Work Center</label>
              <select value={targetWcId} onChange={(e) => setTargetWcId(e.target.value)}>
                <option value="">— Pick WC —</option>
                {wcs.map((w) => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
              </select>
              <label>Estimated cost (₹)</label>
              <input type="number" min="0" value={estCost} onChange={(e) => setEstCost(e.target.value)} />
            </>
          )}
          <label>Dispositioned by *</label>
          <input type="text" value={dispBy} onChange={(e) => setDispBy(e.target.value)} placeholder="Your name (Supervisor/Quality)" />
          <label>Notes</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder={disposition === 'SCRAP' ? 'Why scrapping vs rework?' : 'Optional notes'} />
        </div>
        <div className="nc-modal-foot">
          <button className="nc-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="nc-btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : disposition === 'REWORK' ? 'Spawn Rework Order' : 'Confirm Disposition'}
          </button>
        </div>
      </div>
    </div>
  );
}
